import React, { useState } from 'react';
import {
    Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField,
} from '@mui/material';
import { certApi, type CertKind } from '../../api/certApi';

interface Props {
    open: boolean;
    token: string;
    kind: CertKind;
    onClose: () => void;
    onRequested: () => void;
}

const RequestCertDialog: React.FC<Props> = ({ open, token, kind, onClose, onRequested }) => {
    const [cn, setCn] = useState('');
    const [san, setSan] = useState('');
    const [password, setPassword] = useState('');
    const [validityDays, setValidityDays] = useState('365');
    const [error, setError] = useState<string | undefined>();
    const [submitting, setSubmitting] = useState(false);

    const submit = async () => {
        setError(undefined);
        setSubmitting(true);
        try {
            await certApi.request(token, {
                cn,
                san: san ? san.split(',').map(s => s.trim()).filter(Boolean) : undefined,
                kind,
                p12_password: password,
                validity_days: Number(validityDays) || undefined,
            });
            setCn(''); setSan(''); setPassword(''); setValidityDays('365');
            onRequested();
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
            <DialogTitle>Request new {kind} certificate</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {error && <Alert severity='error'>{error}</Alert>}
                    <Alert severity='info'>
                        Issuance requires manual approval. You'll get a push notification on your
                        registered iOS device(s); the .p12 download appears here once approved.
                    </Alert>
                    <TextField label='Common Name (CN)' value={cn} onChange={e => setCn(e.target.value)} fullWidth required />
                    <TextField label='Subject Alt Names (comma-separated)' value={san} onChange={e => setSan(e.target.value)} fullWidth />
                    <TextField label='.p12 password' type='password' value={password} onChange={e => setPassword(e.target.value)} fullWidth required helperText='Minimum 8 characters' />
                    <TextField label='Validity (days)' type='number' value={validityDays} onChange={e => setValidityDays(e.target.value)} fullWidth />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting}>Cancel</Button>
                <Button onClick={submit} variant='contained' disabled={!cn || password.length < 8 || submitting}>
                    {submitting ? 'Requesting...' : 'Submit request'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RequestCertDialog;
